import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { SORT_ORDER_UPDATE_DATE_DESC } from '../../../common/libs/sortOrder';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified,
  isPublisherAccount,
} from '../../../common/libs/user';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import SideBarStickyContent from '../../../components/SideBarStickyContent';
import SpecialSideLoginBanner from '../../../components/SpecialSideLoginBanner';
import useSpecialPage from '../../../hooks/useSpecialPage';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import {
  findTagByTagName,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';
import withAuth from '../../../server/libs/withAuth';

const TAG_TARGET = '高尿酸血症';
const specialPageSlug = 'hyperuricemia';
const specialPageTitle = '高尿酸血症特集';
const specialPagePath = `/special/${specialPageSlug}`;
const mainImagePath = '/images/special/hyperuricemia_main_1300*720.png';

const userAccessHistoryOperation = `special_HU`;

type PageContext = {
  tag: Tag;
  slideArticles: ArticleHeader[];
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
};

type Tag = {
  tagId: number | null | undefined;
  name: string | null | undefined;
};

async function getSlideArticles(articleIdList: string[], context: any) {
  const articleHeaders = await prisma.article_header.findMany({
    select: {
      article_id: true,
      name: true,
      article_type: true,
      status: true,
      top_img_m: true,
      session_count: true,
      like_count: true,
      disclose_scope: true,
      create_date: true,
      update_date: true,
      user_article_header_author_idTouser: true,
      article_tag_connection: {
        select: {
          tag: true,
        },
      },
      article_page: {
        select: {
          like_count: true,
        },
      },
      article_comments: {
        select: {
          id: true,
        },
      },
    },
    where: {
      article_id: {
        in: articleIdList,
      },
      article_type: 'SLIDE',
      status: 'DONE',
      hidden: false,
      is_deleted: false,
    },
    orderBy: {
      create_date: 'desc',
    },
  });

  // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
  const discloseIds = articleHeaders
    .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
    .filter((v) => !!v);
  // アカウント統合済みのユーザのプロフィールのみ返却される
  const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

  return articleHeaders.reduce((articles: any, article: any) => {
    const author = article.user_article_header_author_idTouser;
    const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

    if (
      shouldNotShow(
        idBaseProfile,
        context.req.user,
        context.req.isAuthenticated(),
        article,
      )
    ) {
      return articles;
    }

    articles.push({
      articleId: article.article_id,
      name: article.name,
      articleType: article.article_type,
      status: article.status as ArticleStatus | null,
      topImageM: article.top_img_m,
      sessionCount: article.session_count || 0,
      likeCount:
        article.like_count +
        article.article_page.reduce(
          (prev: any, curr: any) => prev + curr.like_count,
          0,
        ),
      commentCount: article.article_comments.length,
      discloseScope: article.disclose_scope as DiscloseScope | null,
      createDate: date.toIso8601Jst(article.create_date),
      updateDate: date.toIso8601Jst(article.update_date),
      author: {
        userId: author.user_id,
        userName: idBaseProfile?.userName ?? null,
        // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
        lastName: idBaseProfile?.userName
          ? null
          : idBaseProfile?.lastName ?? author.last_name,
        firstName: idBaseProfile?.userName
          ? null
          : idBaseProfile?.firstName ?? author.first_name,
        updateDateEpochMilliseconds: author.update_date.getTime(),
        profilePhotoS3Key: author.profile_photo_s3_key,
        idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
        antaaAccountType: idBaseProfile?.accountType ?? null,
        antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
        isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
      },
      tags: article.article_tag_connection.map((conn: any) => ({
        id: conn.tag.tag_id,
        name: conn.tag.tag_name,
      })),
    });
    return articles;
  }, []);
}

const SideBanner = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  return (
    <>
      {!isAuthenticated && (
        <SideBarStickyContent stickyHeight={45}>
          <SpecialSideLoginBanner redirectUrl="/special/hyperuricemia" />
        </SideBarStickyContent>
      )}
    </>
  );
};

const HyperuricemiaPage: NextPage<PageContext> = ({
  tag,
  slideArticles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageSlug,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box
                pos="relative"
                mb={2}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
                overflow="hidden"
                display="flex"
              >
                <NextImage
                  src="/images/special/hyperuricemia_main_1300*720.png"
                  alt="高尿酸血症特集"
                  width={1300}
                  height={720}
                  priority
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    企画概要
                  </Text>
                  <Text fontSize="sm">
                    潜在患者数1,300万人と言われ、特に30～40代男性に限れば3割が遭遇する「高尿酸血症」。
                    <br />
                    昔の知識のままで診ていませんか？症状や基本的な対処法を知っていても原因精査や薬剤選択、治療の手順で悩むことも。
                    <br />
                    こちらの特集ページでは痛風/心腎血管疾患のリスクや、薬剤選択のポイントなど、高尿酸血症に関するスライドを公開中！
                  </Text>
                </Box>
                <Box mb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    スライド募集中
                  </Text>
                  <Text fontSize="sm">
                    アンケート回答にて、下記のスライドがリクエストされています。投稿をお待ちしています。
                    <br />
                    ・痛風の治療と再発予防
                    <br />
                    ・無症候性高尿酸血症の治療の意義、賛否の両論、患者さんへの指導方法
                    <br />
                    ・高尿酸血症の心血管リスク・腎障害リスク
                    <br />
                    ・痛風・高尿酸血症の食事療法・栄養指導
                  </Text>
                </Box>
                <Flex justifyContent="flex-end">
                  <NextLink
                    href={`/tagsearch/${
                      tag?.tagId ?? ''
                    }?sortOrder=${SORT_ORDER_UPDATE_DATE_DESC}`}
                    passHref
                  >
                    <Button
                      size="sm"
                      as="a"
                      textAlign="center"
                      display="inline-block"
                      color="brand.500"
                      borderColor="brand.500"
                      borderWidth={1}
                      bg="white"
                      p={4}
                      pl={{ base: 2, md: 4 }}
                      pr={{ base: 2, md: 4 }}
                      height="auto"
                      w={{ base: '50%', md: '30%' }}
                    >
                      スライド一覧
                    </Button>
                  </NextLink>
                  <NextLink href="/article/upload?tags=高尿酸血症" passHref>
                    <Button
                      size="sm"
                      as="a"
                      color="white"
                      bg="brand.500"
                      onClick={() => {
                        sendAnalyticsEvent(
                          'special_contents',
                          'click',
                          'hyperuricemiaUpload',
                        );
                      }}
                      textAlign="center"
                      display="inline-block"
                      p={2}
                      pl={{ base: 2, md: 4 }}
                      pr={{ base: 2, md: 4 }}
                      ml={4}
                      height="auto"
                      w={{ base: '50%', md: '30%' }}
                      _hover={{
                        opacity: '0.8',
                      }}
                    >
                      <Text as="span">#高尿酸血症</Text>
                      <br />
                      <Text as="span" fontSize="xs">
                        でスライド投稿
                      </Text>
                    </Button>
                  </NextLink>
                </Flex>
              </Box>
            </Box>

            {/* スライド一覧 */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {slideArticles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      #{TAG_TARGET} に関するスライド
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={slideArticles}
                      isMobile={isMobile}
                      gaEventLabel={`${TAG_TARGET}_tagsearch`}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_${TAG_TARGET}#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_${TAG_TARGET}#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </GridItem>
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              <SideBanner isAuthenticated={isAuthenticated} />
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default HyperuricemiaPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';
    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const displayTag = await findTagByTagName(TAG_TARGET);
    const articleIdList = await getArticleIdsWithTagIds(
      displayTag ? [displayTag.tag_id] : [],
    );
    const slideArticles: ArticleHeader[] = await getSlideArticles(
      articleIdList,
      context,
    );

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        tag: {
          tagId: displayTag?.tag_id || null,
          name: displayTag?.tag_name || null,
        },
        slideArticles,
        isMobile,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isDemoAccount: hasDemoAuthority(context.req.user?.userRoles),
        isPrivate,
      },
    };
  },
);
